import type {
  CreateFlowConfigRequest,
  FlowStepRequest,
  LLMFlowStepConfigRequest,
  OCRFlowStepConfigRequest,
  LLMConfigOptions,
  LLMConfigOptionsViewModel,
  AgentExecutionRequest,
  FlowConfigViewModel,
  FlowStepViewModel,
  AgentExecutionViewModel,
  ChunkingOptions,
  PageDetectionPolicyOptions,
  PreprocessingOptions,
  OCROptions,
} from "@models/doctype";
import { StepType } from "@models/doctype";
import { joinModelRef } from "@utils/modelRef";

export interface FlowConfigFormData {
  flowBaseId?: string | null;
  flowType: string;
  flowDescription?: string | null;
  steps: FlowStepFormData[];
  preprocessingOptions?: PreprocessingOptions | null;
}

export interface FlowStepFormData {
  stepOrder: number;
  stepType: string;
  llmStepConfig?: LLMStepConfigFormData;
  ocrStepConfig?: OCRStepConfigFormData;
}

export interface LLMStepConfigFormData {
  /** Catalog provider key; joined with `modelName` into the stored reference. */
  provider: string;
  systemPrompt: string;
  userPrompt: string;
  modelName: string;
  configOptions?: LLMConfigOptionsViewModel | null;
  responseSchema?: Record<string, unknown> | null;
  executions?: AgentExecutionFormData[] | null;
  fallbackModel?: string | null;
  chunkingOptions?: ChunkingOptions | null;
  pageDetectionPolicy?: PageDetectionPolicyOptions | null;
  /** Stored reference prefix, kept on save while the provider is unchanged. */
  modelPrefix?: string;
}

export interface OCRStepConfigFormData {
  provider: string;
  modelName: string;
  ocrOptions?: OCROptions | null;
  /** Stored reference prefix, kept on save while the provider is unchanged. */
  modelPrefix?: string;
}

export interface AgentExecutionFormData {
  domainKey: string;
  userPrompt: string;
  responseSchema: Record<string, unknown> | null;
  responseSchemaText: string;
  systemPrompt: string;
  /** Provider of the agent's own model; with an empty model the agent inherits the step's. */
  provider?: string;
  modelName: string;
  configOptions?: LLMConfigOptionsViewModel | null;
  fallbackModel?: string | null;
  /** Stored reference prefix, kept on save while the provider is unchanged. */
  modelPrefix?: string;
}

/** `config_options` keys the contract accepts on DATA_EXTRACTION only. */
const EXTRACTION_ONLY_OPTIONS = ["input_mode", "missing_ocr_policy", "ocr_min_confidence", "ocr_bbox_mode"] as const;

/** Drop unset values (null, undefined, ""): stored configs are sparse. `false` and `0` are kept. */
const sparse = <T extends object>(value: T): T | null => {
  const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== "");
  return entries.length ? (Object.fromEntries(entries) as T) : null;
};

const text = (value: string | null | undefined) => (value && value.trim() ? value : null);

export const buildCreateFlowConfigRequest = (
  formData: FlowConfigFormData,
): CreateFlowConfigRequest => {
  return {
    flow_base_id: formData.flowBaseId || null,
    flow_type: formData.flowType,
    flow_description: formData.flowDescription || null,
    steps: formData.steps.map(buildFlowStepRequest),
    preprocessing_options: formData.preprocessingOptions ? sparse(formData.preprocessingOptions) : null,
  };
};

const buildFlowStepRequest = (stepData: FlowStepFormData): FlowStepRequest => {
  const request: FlowStepRequest = {
    step_order: stepData.stepOrder,
    step_type: stepData.stepType,
  };

  if (stepData.llmStepConfig) {
    request.llm_step_config = buildLLMStepConfigRequest(stepData.stepType, stepData.llmStepConfig);
  }

  if (stepData.ocrStepConfig) {
    request.ocr_step_config = buildOCRStepConfigRequest(stepData.ocrStepConfig);
  }

  return request;
};

/**
 * One LLM step, as the strict contract accepts it for its step type:
 * PAGE_DETECTION takes chunking but no agents, page-detection policy or
 * extraction-only options; DATA_EXTRACTION takes no chunking. A multi-agent
 * step sends no step-level user prompt or schema (each agent has its own).
 */
const buildLLMStepConfigRequest = (
  stepType: string,
  config: LLMStepConfigFormData,
): LLMFlowStepConfigRequest => {
  const isPageDetection = stepType === StepType.PAGE_DETECTION;
  const agents = isPageDetection ? [] : config.executions ?? [];
  const multiAgent = agents.length > 0;

  const request: LLMFlowStepConfigRequest = {
    system_prompt: config.systemPrompt,
    model_name: joinModelRef(config.provider, config.modelName, config.modelPrefix),
  };
  const userPrompt = text(config.userPrompt);
  if (userPrompt && !multiAgent) request.user_prompt = userPrompt;
  if (config.responseSchema && !multiAgent) request.response_schema = config.responseSchema;
  const fallback = text(config.fallbackModel);
  if (fallback) request.fallback_model = fallback;

  const options = config.configOptions ? buildLLMConfigOptions(config.configOptions, isPageDetection) : null;
  if (options) request.config_options = options;

  if (isPageDetection) {
    const chunking = config.chunkingOptions ? sparse(config.chunkingOptions) : null;
    if (chunking) request.chunking_options = chunking;
  } else {
    const policy = config.pageDetectionPolicy ? sparse(config.pageDetectionPolicy) : null;
    if (policy) request.page_detection_policy = policy;
    if (multiAgent) request.executions = agents.map((agent) => buildAgentExecutionRequest(agent, config.provider));
  }

  return request;
};

const buildOCRStepConfigRequest = (
  config: OCRStepConfigFormData,
): OCRFlowStepConfigRequest => {
  const request: OCRFlowStepConfigRequest = {
    model_name: joinModelRef(config.provider, config.modelName, config.modelPrefix),
  };
  const options = config.ocrOptions ? sparse(config.ocrOptions) : null;
  if (options) request.ocr_options = options;
  return request;
};

/** An agent's model override without its own provider is a model of the step's provider. */
const buildAgentExecutionRequest = (
  agent: AgentExecutionFormData,
  stepProvider: string,
): AgentExecutionRequest => {
  const request: AgentExecutionRequest = {
    domain_key: agent.domainKey,
    user_prompt: agent.userPrompt,
    response_schema: agent.responseSchema || {},
  };
  const systemPrompt = text(agent.systemPrompt);
  if (systemPrompt) request.system_prompt = systemPrompt;
  const model = joinModelRef(agent.provider || stepProvider, agent.modelName, agent.modelPrefix);
  if (model) request.model_name = model;
  const fallback = text(agent.fallbackModel);
  if (fallback) request.fallback_model = fallback;
  // Agents always belong to a DATA_EXTRACTION step.
  const options = agent.configOptions ? buildLLMConfigOptions(agent.configOptions, false) : null;
  if (options) request.config_options = options;
  return request;
};

const buildLLMConfigOptions = (
  viewModel: LLMConfigOptionsViewModel,
  isPageDetection: boolean,
): LLMConfigOptions | null => {
  const options: LLMConfigOptions = {
    temperature: viewModel.temperature,
    top_p: viewModel.topP,
    top_k: viewModel.topK,
    max_output_tokens: viewModel.maxOutputTokens,
    thinking_budget: viewModel.thinkingBudget,
    seed: viewModel.seed,
    media_resolution: viewModel.mediaResolution,
    compact_response_schema: viewModel.compactResponseSchema,
    input_mode: viewModel.inputMode,
    missing_ocr_policy: viewModel.missingOcrPolicy,
    ocr_min_confidence: viewModel.ocrMinConfidence,
    ocr_bbox_mode: viewModel.ocrBboxMode,
  };
  if (isPageDetection) for (const key of EXTRACTION_ONLY_OPTIONS) delete options[key];
  return sparse(options);
};

const buildAgentFormData = (
  agent: AgentExecutionViewModel,
): AgentExecutionFormData => ({
  domainKey: agent.domainKey,
  userPrompt: agent.userPrompt,
  responseSchema: agent.responseSchema,
  responseSchemaText: JSON.stringify(agent.responseSchema, null, 2),
  systemPrompt: agent.systemPrompt || "",
  provider: agent.provider,
  modelName: agent.modelName || "",
  modelPrefix: agent.modelPrefix,
  configOptions: agent.configOptions,
  fallbackModel: agent.fallbackModel,
});

export const buildFlowStepFormData = (
  step: FlowStepViewModel,
): FlowStepFormData => ({
  stepOrder: step.stepOrder,
  stepType: step.stepType,
  llmStepConfig: step.llmStepConfig
    ? {
        provider: step.llmStepConfig.provider,
        modelPrefix: step.llmStepConfig.modelPrefix,
        systemPrompt: step.llmStepConfig.systemPrompt,
        userPrompt: step.llmStepConfig.userPrompt,
        modelName: step.llmStepConfig.modelName,
        configOptions: step.llmStepConfig.configOptions,
        responseSchema: step.llmStepConfig.responseSchema,
        executions: step.llmStepConfig.executions?.map(buildAgentFormData) || null,
        fallbackModel: step.llmStepConfig.fallbackModel,
        chunkingOptions: step.llmStepConfig.chunkingOptions,
        pageDetectionPolicy: step.llmStepConfig.pageDetectionPolicy,
      }
    : undefined,
  ocrStepConfig: step.ocrStepConfig
    ? {
        provider: step.ocrStepConfig.provider,
        modelPrefix: step.ocrStepConfig.modelPrefix,
        modelName: step.ocrStepConfig.modelName,
        ocrOptions: step.ocrStepConfig.ocrOptions,
      }
    : undefined,
});

export const buildFlowConfigFormData = (
  flowConfig: FlowConfigViewModel,
): FlowConfigFormData => ({
  flowBaseId: flowConfig.flowBaseId,
  flowType: flowConfig.flowType,
  flowDescription: flowConfig.flowDescription,
  steps: flowConfig.steps.map(buildFlowStepFormData),
  preprocessingOptions: flowConfig.preprocessingOptions,
});
